import {
  accounts,
  assignments as fixtureAssignments,
  children,
  childLinks,
  consents,
  sessionSummaries,
  skillSummaries,
  stepStates as fixtureStepStates,
  supportProfiles as fixtureSupportProfiles,
  homeNotes as fixtureHomeNotes,
} from "../../domain/fixtures";
import type {
  Assignment,
  Child,
  ChildLink,
  Consent,
  ConsentScopeKey,
  HomeNote,
  SessionSummary,
  SkillStepState,
  SkillSummary,
  SupportProfile,
} from "../../domain/types";
import { createConsentState, type ConsentState } from "../../domain/consent";
import { normaliseNote } from "../../domain/assignmentNote";
import { BUILT_GAMES, type LocalizedText } from "../../progress/skillRegistry.generated";

export type { ConsentState };

/** Fixture stand-in for the signed-in account. Replaced by real auth once task 622 ships. */
export const FIXTURE_ACCOUNT_ID = "acc-lena";

/** Resolved per-child link state derived from relation + consent. */
export type LinkState = "active" | "pending" | "revoked";

/** A child as seen by the current account, with resolved state and engagement data. */
export interface LinkedChild {
  child: Child;
  link: ChildLink;
  consent: Consent | undefined;
  linkState: LinkState;
  /** Email of the child's owner — shown as "Shared by …" in viewer rows; undefined for owners. */
  ownerEmail: string | undefined;
  /** Play sessions visible to this account. Empty for pending/revoked links. */
  sessions: SessionSummary[];
  /** Skill summaries visible to this account. Empty for pending/revoked links. */
  skills: SkillSummary[];
  /** Skill-step snapshot (progress scope). Empty for older app versions and for pending/revoked links. */
  stepStates: SkillStepState[];
  /** "How best to support me" (progress scope), or null when none has synced. */
  supportProfile: SupportProfile | null;
  /** Centre suggestions for this child (results scope; RLS returns none without it). Newest first. */
  assignments: Assignment[];
  /** "Seen at home" notes the family shared with this account (progress scope). */
  homeNotes: HomeNote[];
}

/** Newest first. ISO-8601 UTC strings sort lexicographically by time. */
export function newestFirst<T extends { at: string }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

function resolveLinkState(link: ChildLink, consent: Consent | undefined): LinkState {
  if (link.relation === "owner") return "active";
  if (!consent) return "pending";
  if (consent.revokedAt) return "revoked";
  return "active";
}

/** Returns all children linked to `accountId` with resolved state and engagement data. */
export function getLinkedChildren(accountId: string): LinkedChild[] {
  const links = childLinks.filter((l) => l.accountId === accountId);
  return links.flatMap((link) => {
    const child = children.find((c) => c.id === link.childId);
    if (!child) return [];
    const consent = link.consentId
      ? consents.find((c) => c.id === link.consentId)
      : undefined;
    const owner = accounts.find((a) => a.id === child.ownerAccountId);
    const linkState = resolveLinkState(link, consent);
    const canSee = linkState === "active";
    return [
      {
        child,
        link,
        consent,
        linkState,
        ownerEmail: link.relation === "owner" ? undefined : owner?.email,
        sessions: canSee
          ? newestFirst(sessionSummaries.filter((s) => s.childId === child.id))
          : [],
        skills: canSee
          ? skillSummaries.filter((s) => s.childId === child.id)
          : [],
        stepStates:
          canSee && link.scope.includes("progress")
            ? fixtureStepStates.filter((s) => s.childId === child.id)
            : [],
        supportProfile:
          canSee && link.scope.includes("progress")
            ? fixtureSupportProfiles.find((p) => p.childId === child.id) ?? null
            : null,
        assignments:
          canSee && link.scope.includes("results")
            ? newestFirst(getChildAssignments(child.id))
            : [],
        // The demo stands in for RLS: the owner sees their own notes, and Amira's
        // family shared hers with the viewer.
        homeNotes:
          canSee && link.scope.includes("progress")
            ? fixtureHomeNotes.filter((n) => n.childId === child.id)
            : [],
      },
    ];
  });
}

/** Returns the linked child with `childId` for `accountId`, or `undefined` if not linked. */
export function getLinkedChild(
  accountId: string,
  childId: string,
): LinkedChild | undefined {
  return getLinkedChildren(accountId).find((lc) => lc.child.id === childId);
}

// --- Play targets (for AssignPractice) -----------------------------------------

export interface PlayTarget {
  /** A BUILT game id — the family's app matches a suggestion to a game by id. */
  id: string;
  /** English game name (kept for callers that only need one language). */
  label: string;
  /** The game's name in every language the registry has. */
  name: LocalizedText;
  kind: "game";
  /** The catalogue skill_domain (a Skill Garden bed), used for grouping. */
  domain: string;
  /** Free in the app; false = a Premium game (gating.ts FREE_CHARACTERS). */
  free: boolean;
}

/**
 * Assignable targets: the bundled games, grouped by their catalogue domain.
 * Built from the generated registry so a suggestion can only ever name a game
 * the family's app actually has.
 */
export const PLAY_TARGETS: PlayTarget[] = BUILT_GAMES.map((g) => ({
  id: g.id,
  label: g.name.en,
  name: g.name,
  kind: "game" as const,
  domain: g.domain,
  free: g.free,
}));

// Client-side assignment store — the demo stand-in for the assignment table.
const _assignmentStore: Assignment[] = [...fixtureAssignments];

/** Returns all assignments for a child visible to the current session. */
export function getChildAssignments(childId: string): Assignment[] {
  return _assignmentStore.filter((a) => a.childId === childId);
}

/** Records a new assignment in the client-side store and returns it. */
export function createAssignment(
  accountId: string,
  childId: string,
  target: string,
  note?: string,
): Assignment {
  const assignment: Assignment = {
    id: `assign-${Date.now()}`,
    childId,
    assignedByAccountId: accountId,
    target,
    status: "assigned",
    at: new Date().toISOString(),
    note: normaliseNote(note),
  };
  _assignmentStore.push(assignment);
  return assignment;
}

// --- Sharing & consent (owner view) -------------------------------------------

/** A viewer linked to a child owned by the current account. */
export interface LinkedViewer {
  accountId: string;
  email: string;
  /** Scopes held at load time (initial fixture state). */
  scopes: ConsentScopeKey[];
  linkState: LinkState;
}

/** An owned child alongside all adults currently linked as viewers. */
export interface OwnedChildWithViewers {
  child: Child;
  viewers: LinkedViewer[];
}

/**
 * Returns all children owned by `accountId`, each with their linked viewers.
 * Used by the Owner section of the Sharing & consent screen.
 */
export function getOwnedChildrenWithViewers(
  accountId: string,
): OwnedChildWithViewers[] {
  const ownedLinks = childLinks.filter(
    (l) => l.accountId === accountId && l.relation === "owner",
  );
  return ownedLinks.flatMap((ownerLink) => {
    const child = children.find((c) => c.id === ownerLink.childId);
    if (!child) return [];
    const viewerLinks = childLinks.filter(
      (l) => l.childId === ownerLink.childId && l.relation === "viewer",
    );
    const viewers: LinkedViewer[] = viewerLinks.flatMap((vl) => {
      const account = accounts.find((a) => a.id === vl.accountId);
      if (!account) return [];
      const consent = vl.consentId
        ? consents.find((c) => c.id === vl.consentId)
        : undefined;
      return [
        {
          accountId: vl.accountId,
          email: account.email,
          scopes: vl.scope,
          linkState: resolveLinkState(vl, consent),
        },
      ];
    });
    return [{ child, viewers }];
  });
}

/**
 * Builds an initial `ConsentState` for `childId` from fixture data.
 * Active (non-revoked, non-empty) viewer grants are pre-loaded.
 * Pending and revoked viewers appear with no grants — owners can grant from here.
 */
export function buildConsentState(childId: string): ConsentState {
  const child = children.find((c) => c.id === childId);
  if (!child) return createConsentState(childId, "unknown");
  const viewerLinks = childLinks.filter(
    (l) => l.childId === childId && l.relation === "viewer",
  );
  const grants: Record<string, readonly ConsentScopeKey[]> = {};
  for (const link of viewerLinks) {
    const consent = link.consentId
      ? consents.find((c) => c.id === link.consentId)
      : undefined;
    if (consent && !consent.revokedAt && link.scope.length > 0) {
      grants[link.accountId] = link.scope;
    }
  }
  return { childId, ownerAccountId: child.ownerAccountId, grants };
}
